import ApiService from '@/service/ApiService';
import { useLoadingStore } from '@/store/loading-store';
import { useReservationStore } from '@/store/reservation-store';
import { useReservationCartStore } from '@/store/reservationCart-store';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

export const useAuthStore = defineStore('auth', () => {
    const reservationStore = useReservationStore();
    const cartStore = useReservationCartStore();
    const loadingStore = useLoadingStore();
    // 상태 정의 (ref 사용)
    const accessToken = ref(localStorage.getItem('accessToken') || null);
    const refreshToken = ref(localStorage.getItem('refreshToken') || null);
    const accessTokenExpireDate = ref(localStorage.getItem('accessTokenExpireDate') || null);
    const refreshTokenExpireDate = ref(localStorage.getItem('refreshTokenExpireDate') || null);
    const user = ref(null); // 상세 사용자 정보 객체 (API 응답에 따라 활용)
    const permissions = ref([]); // 권한 목록
    const userId = ref(localStorage.getItem('userId') || null); // 사용자 ID (로그인 ID)
    const userName = ref(localStorage.getItem('userName') || null); // 사용자 이름
    const mobile = ref(localStorage.getItem('mobile') || null); // 사용자 연락처 (원본 형식)
    const birthDate = ref(localStorage.getItem('birthDate') || null); // 사용자 생년월일
    const customerType = ref(localStorage.getItem('customerType') || null);
    const corporationName = ref(localStorage.getItem('corporationName') || null);

    const isRefreshing = ref(false); // 토큰 갱신 중 여부

    // 사용자 기본 정보를 객체로 묶어 반환하는 계산된 속성
    const getUserInfo = computed(() => {
        return {
            userId: userId.value,
            userName: userName.value,
            mobile: mobile.value,
            birthDate: birthDate.value,
            customerType: customerType.value,
            corporationName: corporationName.value
        };
    });

    function initializeAuth() {
        const storedAccessToken = localStorage.getItem('accessToken');
        const storedRefreshToken = localStorage.getItem('refreshToken');
        const storedAccessTokenExpireDate = localStorage.getItem('accessTokenExpireDate');
        const storedRefreshTokenExpireDate = localStorage.getItem('refreshTokenExpireDate');

        if (storedAccessToken && storedRefreshToken && storedAccessTokenExpireDate && storedRefreshTokenExpireDate) {
            accessToken.value = storedAccessToken;
            refreshToken.value = storedRefreshToken;
            // Date 객체로 변환하여 저장하는 것이 만료 시간 비교에 더 용이합니다.
            accessTokenExpireDate.value = new Date(storedAccessTokenExpireDate);
            refreshTokenExpireDate.value = new Date(storedRefreshTokenExpireDate);

            ApiService.setHeader('GID-AUTH', storedAccessToken);

            if (isAccessTokenExpired()) {
                if (isRefreshTokenExpired()) {
                    logout();
                } else {
                    // refreshTokens(); // 필요시 토큰 갱신 로직 호출
                }
            }
        }
    }

    function setTokens(newAccessToken, newRefreshToken, newAccessTokenExpireDate, newRefreshTokenExpireDate) {
        accessToken.value = newAccessToken;
        refreshToken.value = newRefreshToken;
        accessTokenExpireDate.value = newAccessTokenExpireDate; // new Date(newAccessTokenExpireDate) 형식일 수 있음
        refreshTokenExpireDate.value = newRefreshTokenExpireDate; // new Date(newRefreshTokenExpireDate) 형식일 수 있음

        localStorage.setItem('accessToken', newAccessToken);
        localStorage.setItem('refreshToken', newRefreshToken);
        localStorage.setItem('accessTokenExpireDate', newAccessTokenExpireDate);
        localStorage.setItem('refreshTokenExpireDate', newRefreshTokenExpireDate);

        ApiService.setHeader('GID-AUTH', `${newAccessToken}`);
    }

    function setUser(newUser) {
        user.value = newUser;
        // 필요하다면 newUser 객체에서 다른 상태값(userName, mobile 등)도 업데이트
    }

    function setPermissions(newPermissions) {
        permissions.value = newPermissions;
    }

    function setUserId(id) {
        userId.value = id;
        localStorage.setItem('userId', id);
    }

    function setUserName(name) {
        userName.value = name;
        localStorage.setItem('userName', name);
    }

    function setMobile(newMobile) {
        mobile.value = newMobile;
        localStorage.setItem('mobile', newMobile);
    }

    function setBirthDate(newBirthDate) {
        birthDate.value = newBirthDate;
        localStorage.setItem('birthDate', newBirthDate);
    }

    function setCustomerType(newCustomerType) {
        customerType.value = newCustomerType;
        localStorage.setItem('customerType', newCustomerType);
    }

    function setCorporationName(newCorporationName) {
        corporationName.value = newCorporationName;
        localStorage.setItem('corporationName', newCorporationName);
    }

    async function login(credentials) {
        loadingStore.showLoading();
        try {
            const response = await ApiService.post('/api/customer/login', credentials);
            if (response.data.success) {
                const { access_token, refresh_token, email, name, mobile, birth_date, customer_type, corporation_name, business_no, corporation_address, corporation_address_detail, corporation_manager, corporation_manager_contact } =
                    response.data.data;
                if (access_token && refresh_token) {
                    setTokens(access_token.value, refresh_token.value, access_token.expire_date, refresh_token.expire_date);
                    // ApiService 헤더 설정은 setTokens 내에서 처리됨
                    setUserId(email); // API 응답에 email_id가 없다면 credentials.email_id 사용
                    setUserName(name);
                    setMobile(mobile);
                    setBirthDate(birth_date);
                    setCustomerType(customer_type);
                    setCorporationName(corporation_name);

                    // 예약 상태관리 업데이트 (타입, 법인정보도 업데이트 해야함.)
                    reservationStore.updateReservationData({
                        customer_login_id: credentials.email_id,
                        customer_name: name,
                        birthdate: birth_date?.replaceAll('-', ''),
                        phone: mobile,
                        customer_type: customer_type,
                        corporation_name: corporation_name || '',
                        business_no: business_no || '',
                        corporation_manager: corporation_manager || '',
                        corporation_manager_contact: corporation_manager_contact || '',
                        corporation_address: corporation_address || '',
                        corporation_address_detail: corporation_address_detail || '',
                        driver_name: name,
                        driver_phone: mobile,
                        driver_birth_date: birth_date?.replaceAll('-', ''),
                        insurance_age: calculateInsuranceAge(birth_date?.replaceAll('-', ''))
                    });

                    // 사용자 상세 정보 설정 (API 응답 구조에 따라)
                    // setUser(response.data.data);

                    // 라우터 관련 로직 (필요시)
                    // const routerStore = useRouterStore();
                    // await router.isReady();

                    return true; // 성공 여부 반환
                }
            } else {
                // API에서 success: false로 응답한 경우
                throw new Error(response.data.msg || 'Login failed');
            }
        } catch (error) {
            console.error('Login error:', error);
            throw error; // 에러를 다시 던져서 호출한 쪽에서 처리하도록 함
        } finally {
            loadingStore.hideLoading();
        }
        return false; // 실패 시
    }

    async function sociallogin(data) {
        try {
            if (data && data.access_token && data.refresh_token) {
                setTokens(data.access_token.value, data.refresh_token.value, data.access_token.expire_date, data.refresh_token.expire_date);
                // ApiService 헤더 설정은 setTokens 내에서 처리됨
                setUserId(data.email);
                setUserName(data.name);
                setMobile(data.mobile);
                setBirthDate(data.birth_date);
                setCustomerType('INDV');
                setCorporationName('');

                // 예약 상태관리 업데이트 (소셜은 일반사용자만 해당)
                reservationStore.updateReservationData({
                    customer_login_id: data.email,
                    customer_name: data.name,
                    birthdate: data.birth_date?.replaceAll('-', ''),
                    phone: data.mobile,
                    customer_type: 'INDV',
                    corporation_name: '',
                    business_no: '',
                    corporation_manager: '',
                    corporation_manager_contact: '',
                    corporation_address: '',
                    corporation_address_detail: '',
                    driver_name: data.name,
                    driver_phone: data.mobile,
                    driver_birth_date: data.birth_date?.replaceAll('-', ''),
                    insurance_age: calculateInsuranceAge(data.birth_date?.replaceAll('-', ''))
                });

                // 사용자 상세 정보 설정 (API 응답 구조에 따라)
                // setUser(data);

                // 라우터 관련 로직 (필요시)
                // const routerStore = useRouterStore();
                // await router.isReady();

                return true;
            }
        } catch (error) {
            console.error('Social login error:', error);
            throw error;
        }
        return false;
    }

    async function refreshUserInfo() {
        try {
            const fetchInfo = () => ApiService.get('/api/customer/info');

            let response = await fetchInfo();
            if (response.data.success && Array.isArray(response.data.data) && response.data.data.length > 0) {
                const userData = response.data.data[0];
                setUserName(userData.name);
                setMobile(userData.phone);
                setBirthDate(userData.birthdate);
                setCustomerType(userData.customer_type);
                setCorporationName(userData.corporation_name);

                reservationStore.updateReservationData({
                    customer_login_id: userData.login_id,
                    customer_name: userData.name,
                    birthdate: userData.birthdate?.replaceAll('-', ''),
                    phone: userData.phone,
                    customer_type: userData.customer_type,
                    corporation_name: userData.corporation_name || '',
                    business_no: userData.business_no || '',
                    corporation_manager: userData.corporation_manager || '',
                    corporation_manager_contact: userData.corporation_manager_contact || '',
                    corporation_address: userData.corporation_address || '',
                    corporation_address_detail: userData.corporation_address_detail || '',
                    driver_name: userData.name,
                    driver_phone: userData.phone,
                    driver_birth_date: userData.birthdate?.replaceAll('-', ''),
                    insurance_age: calculateInsuranceAge(userData.birthdate?.replaceAll('-', ''))
                });

                return true;
            } else {
                console.error('사용자 정보를 불러오지 못했습니다.');
                return false;
            }
        } catch (error) {
            if (error.response?.status === 403) {
                console.warn('403 발생, 토큰 갱신 시도');
                const ok = await refreshTokens();
                if (ok) {
                    // 갱신 성공하면 다시 호출
                    try {
                        const retry = await ApiService.get('/api/customer/info');
                        if (retry.data.success && retry.data.data.length) {
                            // 정상 처리…
                            return true;
                        }
                    } catch (e2) {
                        console.error('재시도 중 오류:', e2);
                    }
                }
            }
            console.error('refreshUserInfo 호출 중 오류 발생:', error);
            return false;
        }
    }

    async function getPermissions() {
        // 권한 가져오는 API 호출 로직 (예시)
        try {
            // const response = await ApiService.get('/api/authorization/manager');
            // setPermissions(response.data);
        } catch (error) {
            console.error('Fetch permissions error:', error);
        }
    }

    function logout() {
        accessToken.value = null;
        refreshToken.value = null;
        accessTokenExpireDate.value = null;
        refreshTokenExpireDate.value = null;
        user.value = null;
        permissions.value = [];
        userId.value = null;
        userName.value = null;
        mobile.value = null;
        birthDate.value = null;

        // 예약 상태관리 초기화
        reservationStore.updateReservationData({
            customer_login_id: '',
            customer_name: '',
            birthdate: '',
            phone: '',
            customer_type: 'INDV',
            corporation_name: '',
            corporation_manager: '',
            corporation_manager_contact: '',
            business_no: '',
            corporation_address: '',
            corporation_address_detail: '',
            driver_name: '',
            driver_phone: '',
            driver_birth_date: '',
            insurance_age: ''
        });

        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('accessTokenExpireDate');
        localStorage.removeItem('refreshTokenExpireDate');
        localStorage.removeItem('userId');
        localStorage.removeItem('userName');
        localStorage.removeItem('mobile');
        localStorage.removeItem('birthDate');
        localStorage.removeItem('customerType');
        localStorage.removeItem('corporationName');

        ApiService.removeHeader('GID-AUTH');

        // 라우터 관련 스토어 초기화 (필요시)
        // const routerStore = useRouterStore();
        // routerStore.reset(); // 예시: reset 액션이 있다고 가정

        //router.push('/login'); // 로그인 페이지로 이동
    }

    function hasPermission(permission) {
        return permissions.value.includes(permission);
    }

    function checkAuth() {
        const storedAccessToken = localStorage.getItem('accessToken');
        const storedRefreshToken = localStorage.getItem('refreshToken');

        // 토큰이 없으면 인증 실패
        if (!storedAccessToken || !storedRefreshToken) {
            return false;
        }

        // 토큰이 있으면 상태 업데이트
        if (!accessToken.value) {
            initializeAuth();
        }

        // 액세스 토큰 만료 확인
        if (isAccessTokenExpired()) {
            // 리프레시 토큰도 만료되었으면 로그아웃
            if (isRefreshTokenExpired()) {
                logout();
                return false;
            }

            // 리프레시 토큰이 유효하면 갱신 시도
            return refreshTokens();
        }

        return true;
    }

    function isTokenExpired(expireDate) {
        // expireDate가 유효한 Date 객체인지 또는 날짜 문자열인지 확인 후 비교
        if (!expireDate) return true;
        try {
            return new Date(expireDate) < new Date();
        } catch (e) {
            return true; // 유효하지 않은 날짜 형식이면 만료된 것으로 간주
        }
    }

    function isAccessTokenExpired() {
        // accessTokenExpireDate는 Date 객체일 수 있으므로 바로 비교
        return isTokenExpired(accessTokenExpireDate.value);
    }

    function isRefreshTokenExpired() {
        return isTokenExpired(refreshTokenExpireDate.value);
    }

    async function refreshTokens() {
        if (isRefreshing.value) return; // 이미 갱신 중이면 중복 실행 방지
        isRefreshing.value = true;
        try {
            // 토큰 갱신 API 호출
            const response = await ApiService.post('/api/customer/token', {
                refresh_token: refreshToken.value
            });
            if (response.data.success) {
                const { access_token, refresh_token } = response.data.data;
                setTokens(access_token, refresh_token, access_token.expire_date, refresh_token.expire_date);
                console.log('Tokens refreshed successfully.');
                isRefreshing.value = false;
                return true;
            } else {
                throw new Error(response.data.msg || 'Token refresh failed');
            }
        } catch (error) {
            console.error('Token refresh error:', error);
            // 갱신 실패 시 로그아웃 처리
            logout();
            isRefreshing.value = false;
            return false;
        }
    }

    function calculateInsuranceAge(birthdate) {
        if (!birthdate || birthdate.length !== 8) return '';

        const birthYear = parseInt(birthdate.substring(0, 4), 10);
        const birthMonth = parseInt(birthdate.substring(4, 6), 10);
        const birthDay = parseInt(birthdate.substring(6, 8), 10);

        const today = new Date();
        let age = today.getFullYear() - birthYear;

        const monthDiff = today.getMonth() + 1 - birthMonth;
        const dayDiff = today.getDate() - birthDay;

        if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
            age--;
        }

        console.log(age.toString());

        return age.toString();
    }

    // 스토어에서 사용할 상태, 계산된 속성, 액션들을 반환
    return {
        accessToken,
        refreshToken,
        accessTokenExpireDate,
        refreshTokenExpireDate,
        user,
        permissions,
        userId,
        userName,
        mobile,
        birthDate,
        customerType,
        corporationName,
        isRefreshing,
        getUserInfo,
        initializeAuth,
        setTokens,
        setUser,
        setPermissions,
        setUserId,
        setUserName,
        setMobile,
        setBirthDate,
        setCustomerType,
        setCorporationName,
        login,
        sociallogin,
        getPermissions,
        logout,
        hasPermission,
        checkAuth,
        isTokenExpired, // 외부에 노출할 필요는 없을 수 있음
        isAccessTokenExpired,
        isRefreshTokenExpired,
        refreshTokens,
        refreshUserInfo
    };
});
